import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Content,
  Dialog,
  DialogContainer,
  Item,
  TextArea,
  View,
  Heading,
  ComboBox,
  Key,
  Flex,
  Checkbox,
  Divider,
  ProgressCircle,
} from "@adobe/react-spectrum";
import Emitter from "../../../emitter";
import {
  API_AGENT_GROUP_STATUS,
  API_CHAT_PATCH_ATTRIBUTE,
  API_CHAT_TRANSFER,
  TRANSFER_CHAT_IN_PROGRESS,
  TRANSFER_NOTES_LENGTH,
} from "../../../constants/constants";

import { RootState } from "../../../store/storeConfig";
import {
  ConnectTransferEndppoint,
  IndividualChat,
} from "../../../types/chat.type";
import { debounce } from "lodash";
import { CHAT_TRANSFER_TO_OPTIONS } from "../constants";
import { AgentState, AssignQueues } from "../../../types/agent.type";
import { addTransferNoteAttribute } from "../../../api/session-api";

const ChatTransferDialog = (props: any) => {
  const allChats = useSelector((state: RootState) => state.chat);
  const activeChat = allChats.chats.find(
    (chat: IndividualChat) => chat.id === allChats.activeChatID
  );
  const prevChatIdRef = useRef(allChats.activeChatID);
  const agent = useSelector((state: RootState) => state.agent);

  const [transferNotes, setTransferNotes] = useState<any>(
    activeChat?.customer?.attributes?.notes
  );
  const [transferChatId, setTransferChatId] = useState("");
  const [transferToKeyId, setTransferToKeyId] = useState<Key | null>(
    CHAT_TRANSFER_TO_OPTIONS[0].id
  );
  const [isTransferInProgress, setIsTransferInProgress] =
    useState<boolean>(false);
  const [transferToValueId, setTransferToValueId] = useState<Key | null>(null);
  const [transferToSelectOptions, setTransferToSelectOptions] = useState<any>({
    Person: [],
    Queue: [],
  });
  const [filterValue, setFilterValue] = useState("");

  useEffect(() => {
    if (
      isTransferInProgress &&
      prevChatIdRef.current &&
      prevChatIdRef.current !== allChats.activeChatID
    ) {
      // Chat ended
      setIsTransferInProgress(false);
      props.onClose(false);
    }
  }, [allChats.activeChatID, isTransferInProgress]);

  useEffect(() => {
    Emitter.on(API_AGENT_GROUP_STATUS, (data) => {
      setTransferToSelectOptions((prevState: any) => ({
        Person: data.body.entities.map((q: AssignQueues) => {
          return { id: q.id, name: q.name };
        }),
        Queue: prevState.Queue,
      }));
    });

    Emitter.on(TRANSFER_CHAT_IN_PROGRESS, (event) => {
      setIsTransferInProgress(event.body.isTransferInProgress);
      setTransferChatId(
        event.body.isTransferInProgress ? event.body.conversationId : ""
      );

      if (event.body.isTransferInProgress) {
        prevChatIdRef.current = allChats.activeChatID;
      }
    });

    return () => {
      Emitter.off(API_AGENT_GROUP_STATUS);
      Emitter.off(TRANSFER_CHAT_IN_PROGRESS);
    };
  }, [allChats.activeChatID]);

  useEffect(() => {
    if (activeChat?.id) {
      setTransferToSelectOptions({
        Person: [],
        Queue:
          activeChat?.allowedTransferEndpoints
            ?.filter(
              (q: ConnectTransferEndppoint) =>
                q.id && q.name && q.quick_connect_type && q.arn
            )
            .map((q: ConnectTransferEndppoint) => ({
              id: q.id,
              name: q.name,
              quick_connect_type: q.quick_connect_type,
              arn: q.arn,
            })) || [],
      });
    }
  }, [activeChat]);

  const patchAttribute = useMemo(
    () =>
      debounce((notes: string) => {
        addTransferNoteAttribute(allChats?.activeChatID, notes || "");
        Emitter.emit(API_CHAT_PATCH_ATTRIBUTE, {
          body: {
            notes: notes || "",
            attributes: { notes: notes || "" },
            participantId: activeChat?.self?.id,
            conversationId: activeChat?.id,
          },
        });
        setTransferNotes(notes);
      }, 500),
    [allChats?.activeChatID, activeChat?.id, activeChat?.self?.id]
  );

  const blindTransfer = () => {
    if (!transferToValueId) return;

    setIsTransferInProgress(true);
    setTransferChatId(allChats.activeChatID);
    prevChatIdRef.current = allChats.activeChatID;

    Emitter.emit(TRANSFER_CHAT_IN_PROGRESS, {
      body: {
        isTransferInProgress: true,
        conversationId: allChats.activeChatID,
      },
    });

    const fromQueue = activeChat?.customer?.attributes?.QUEUE;
    const queuePrefix = fromQueue ? `${fromQueue}, ` : "";
    const timestamp = new Date().toString().split(" ")[4];
    const agentName = agent.email.split("@")[0];
    const appendNote = `\n~${queuePrefix}Transferred By: ${agentName} on ${timestamp}`;

    let updatedNotes = transferNotes || "";
    let latestTransferNotes = updatedNotes;

    if (updatedNotes.length < TRANSFER_NOTES_LENGTH - 3) {
      updatedNotes += appendNote;

      if (updatedNotes.length > TRANSFER_NOTES_LENGTH) {
        updatedNotes =
          updatedNotes.substring(0, TRANSFER_NOTES_LENGTH - 3) + "...";
      }

      setTransferNotes(updatedNotes);
      latestTransferNotes = updatedNotes;
    }

    const transferEndpoint = transferToSelectOptions.Queue.find(
      (el: AgentState) => el.id === transferToValueId
    );

    const transferBody = {
      queueId: transferToValueId,
      participantId: activeChat?.self?.id,
      conversationId: activeChat?.id,
      transferEndpoint,
    };

    patchAttribute(latestTransferNotes);
    setTimeout(() => {
      Emitter.emit(API_CHAT_TRANSFER, { body: transferBody });
    }, 0);
  };

  const updateNotes = (notes: string) => {
    setTransferNotes(notes);
    patchAttribute(notes);
  };

  const resetTransfer = () => {
    setTransferToValueId(null);
    props.onClose(false);
  };

  const transferTypeName =
    CHAT_TRANSFER_TO_OPTIONS.find((item) => item.id === transferToKeyId)
      ?.name || "Queue";

  const transferOptions = transferToSelectOptions[transferTypeName] || [];

  const filteredTransferOptions = useMemo(() => {
    return transferOptions.filter((item: any) =>
      item.name.toLowerCase().includes(filterValue.toLowerCase())
    );
  }, [transferOptions, filterValue]);

  return (
    <DialogContainer onDismiss={() => props.onClose(false)}>
      {props.isOpen && (
        <Dialog size={"S"} UNSAFE_className="custom-transfer-dialog">
          <Heading level={4}>
            <Flex
              UNSAFE_className="statusDialogHeading"
              justifyContent={"space-between"}
            >
              <span className="dialogHeading">Transfer To</span>
              <span
                className="closeDialogButton"
                onClick={() => props.onClose(false)}
              >
                x
              </span>
            </Flex>
          </Heading>
          <Divider marginBottom={"size-0"} />

          <Content UNSAFE_className="transferDialogBox" marginTop={"20px"}>
            <div className={"transferDialogContent"}>
              {isTransferInProgress ? (
                <Flex
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="size-100"
                >
                  <ProgressCircle
                    aria-label="Transferring..."
                    isIndeterminate
                    size="L"
                  />
                  <strong>Transferring...</strong>
                </Flex>
              ) : (
                <>
                  <div className={"transferContentHeading"}>
                    <div className="transferTypeSelect">
                      <ComboBox
                        aria-label={"transferTypeSelect"}
                        width="100px"
                        defaultItems={CHAT_TRANSFER_TO_OPTIONS}
                        onSelectionChange={setTransferToKeyId}
                        selectedKey={transferToKeyId}
                      >
                        {(item) => <Item>{item?.name}</Item>}
                      </ComboBox>
                    </div>
                    <div className="transferSelectDropdown">
                      <ComboBox
                        aria-label="transferValueSelect"
                        items={filteredTransferOptions}
                        selectedKey={transferToValueId}
                        onSelectionChange={(id) => {
                          setTransferToValueId(id);
                          const selectedItem = filteredTransferOptions.find(
                            (item: any) => item.id === id
                          );
                          if (selectedItem) {
                            setFilterValue(selectedItem.name);
                          }
                        }}
                        inputValue={filterValue}
                        onInputChange={setFilterValue}
                      >
                        {(item: any) => (
                          <Item key={item.id}>{item?.name ?? "Unknown"}</Item>
                        )}
                      </ComboBox>
                    </div>
                  </div>

                  <div className="transferContent">
                    <div className={"transferNotesHeading"}>
                      <span>Add Transfer Notes</span>
                      <span>
                        {transferNotes
                          ? `${
                              TRANSFER_NOTES_LENGTH - transferNotes?.length
                            }/${TRANSFER_NOTES_LENGTH}`
                          : ""}
                      </span>
                    </div>
                    <TextArea
                      aria-label={"transferNotesTextarea"}
                      maxLength={TRANSFER_NOTES_LENGTH}
                      value={transferNotes}
                      onChange={updateNotes}
                      isQuiet
                      width={"100%"}
                      UNSAFE_className="transferNotesTextarea"
                    />
                    <View UNSAFE_className={"transferDialogFooter"}>
                      <Flex alignItems={"center"} justifyContent={"center"}>
                        <Checkbox
                          aria-label={"blindTransferCheckbox"}
                          isEmphasized={true}
                          isDisabled={true}
                          isSelected={true}
                          UNSAFE_className="blindTransferCheckbox-title"
                        >
                          Blind Transfer
                        </Checkbox>
                      </Flex>
                      <div className="transferDialogFooterButtons">
                        <Button
                          variant="negative"
                          isDisabled={isTransferInProgress}
                          onPress={resetTransfer}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="cta"
                          marginStart="15px"
                          UNSAFE_className="transferDialogContinueButton"
                          isDisabled={!transferToValueId}
                          onPress={blindTransfer}
                        >
                          Continue
                        </Button>
                      </div>
                    </View>
                  </div>
                </>
              )}
            </div>
          </Content>
        </Dialog>
      )}
    </DialogContainer>
  );
};

export default ChatTransferDialog;
